// API service for daily tips
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5001/api'
  : '/api';

export interface DailyTip {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  published_at: string;
  read_time: number;
  featured: boolean;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string[] | string;
  campaign_id?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  tips?: DailyTip[];
  tip?: DailyTip;
}

// Fetch all daily tips
export const getAllDailyTips = async (): Promise<DailyTip[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/daily-tips`);
    const result: ApiResponse<DailyTip[]> = await response.json();
    
    if (result.success && result.tips) {
      return result.tips;
    } else {
      console.error('Failed to fetch daily tips:', result.message);
      return [];
    }
  } catch (error) {
    console.error('Error fetching daily tips:', error);
    return [];
  }
};

// Fetch daily tip by slug
export const getDailyTipBySlug = async (slug: string): Promise<DailyTip | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/daily-tips/${slug}`);
    const result: ApiResponse<DailyTip> = await response.json();
    
    if (result.success && result.tip) {
      return result.tip;
    } else {
      console.error('Failed to fetch daily tip:', result.message);
      return null;
    }
  } catch (error) {
    console.error('Error fetching daily tip:', error);
    return null;
  }
};

// Fetch daily tips by category
export const getDailyTipsByCategory = async (category: string): Promise<DailyTip[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/daily-tips/category/${category}`);
    const result: ApiResponse<DailyTip[]> = await response.json();
    
    if (result.success && result.tips) {
      return result.tips;
    } else {
      console.error('Failed to fetch daily tips by category:', result.message);
      return [];
    }
  } catch (error) {
    console.error('Error fetching daily tips by category:', error);
    return [];
  }
};

// Search daily tips
export const searchDailyTips = async (query: string): Promise<DailyTip[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/daily-tips/search/${encodeURIComponent(query)}`);
    const result: ApiResponse<DailyTip[]> = await response.json();
    
    if (result.success && result.tips) {
      return result.tips;
    } else {
      console.error('Failed to search daily tips:', result.message);
      return [];
    }
  } catch (error) {
    console.error('Error searching daily tips:', error);
    return [];
  }
};

// Fetch featured daily tips
export const getFeaturedDailyTips = async (limit: number = 6): Promise<DailyTip[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/daily-tips/featured/${limit}`);
    const result: ApiResponse<DailyTip[]> = await response.json();
    
    if (result.success && result.tips) {
      return result.tips;
    } else {
      console.error('Failed to fetch featured daily tips:', result.message);
      return [];
    }
  } catch (error) {
    console.error('Error fetching featured daily tips:', error);
    return [];
  }
};

// Legacy interface for backward compatibility
export interface HealthHack extends DailyTip {}
export const getAllHealthHacks = getAllDailyTips;
export const getHealthHackBySlug = getDailyTipBySlug;
export const getHealthHacksByCategory = getDailyTipsByCategory;
export const searchHealthHacks = searchDailyTips;
export const getFeaturedHealthHacks = getFeaturedDailyTips; 